import PageHeader from "@/components/common/PageHeader";
import { backgroundDbWorkRefetchIntervalMs } from "@/lib/background-db-work-pause";
import { monitoringService } from "@/services/monitoring.service";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import toast from "react-hot-toast";

function Card({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`rounded-2xl border border-slate-200 bg-white p-6 shadow-sm ${className}`}
    >
      {children}
    </div>
  );
}

function StatCard({
  label,
  value,
  color = "slate",
}: {
  label: string;
  value: string | number;
  color?: string;
}) {
  const colorMap: Record<string, string> = {
    green: "text-green-600",
    red: "text-red-600",
    orange: "text-orange-600",
    blue: "text-blue-600",
    slate: "text-slate-900",
  };

  return (
    <Card>
      <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">
        {label}
      </p>
      <p
        className={`text-2xl font-black mt-1 ${colorMap[color] || colorMap.slate}`}
      >
        {value}
      </p>
    </Card>
  );
}

const CONFLICT_STATUS_BADGE: Record<string, string> = {
  pending: "bg-yellow-50 text-yellow-600",
  resolved: "bg-green-50 text-green-600",
  auto_resolved: "bg-blue-50 text-blue-600",
};

export default function SyncHealthPage() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const limit = 50;

  // Overview stats
  const { data: overview } = useQuery({
    queryKey: ["sync-overview"],
    queryFn: () => monitoringService.getSyncOverview(),
    refetchInterval: () => backgroundDbWorkRefetchIntervalMs(30_000),
  });

  // Per-tenant sync grid
  const { data: gridData, isLoading: gridLoading } = useQuery({
    queryKey: ["sync-grid", page],
    queryFn: () => monitoringService.getSyncGrid(page, limit),
  });

  // Sync conflicts
  const { data: conflicts } = useQuery({
    queryKey: ["sync-conflicts"],
    queryFn: () => monitoringService.getSyncConflicts({ limit: 50 }),
  });

  const grid = gridData?.data || gridData || [];
  const total = gridData?.total || grid.length;
  const conflictList = conflicts?.data || conflicts || [];
  const resolve = useMutation({
    mutationFn: ({ id, strategy }: { id: string; strategy: string }) =>
      monitoringService.resolveSyncConflict(
        id,
        strategy,
        window.prompt("Resolution evidence / notes") || undefined,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sync-conflicts"] });
      queryClient.invalidateQueries({ queryKey: ["sync-overview"] });
      toast.success("Sync conflict resolved");
    },
    onError: () => toast.error("Conflict resolution failed"),
  });

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title="Sync Health"
        subtitle="Monitor synchronization status and conflicts across all tenants"
      />

      {/* Overview Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          label="Syncs (24h)"
          value={overview?.totalSyncs24h ?? "—"}
          color="blue"
        />
        <StatCard
          label="Success Rate"
          value={
            overview?.successRate != null
              ? `${overview.successRate.toFixed(1)}%`
              : "—"
          }
          color="green"
        />
        <StatCard
          label="Pending"
          value={overview?.pendingCount ?? "—"}
          color="orange"
        />
        <StatCard
          label="Failed"
          value={overview?.failedCount ?? "—"}
          color="red"
        />
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <StatCard
          label="Conflicts (24h)"
          value={overview?.conflictCount ?? "—"}
          color="orange"
        />
        <StatCard
          label="Unresolved Conflicts"
          value={overview?.unresolvedConflicts ?? "—"}
          color={overview?.unresolvedConflicts > 0 ? "red" : "green"}
        />
        <StatCard
          label="Avg Duration"
          value={
            overview?.avgDurationMs != null
              ? `${overview.avgDurationMs.toFixed(0)} ms`
              : "—"
          }
          color="slate"
        />
      </div>

      {/* Per-Tenant Sync Grid */}
      <Card>
        <h3 className="text-sm font-semibold uppercase tracking-widest text-slate-400 mb-4">
          Per-Tenant Sync Status
        </h3>
        {gridLoading ? (
          <div className="animate-pulse space-y-2">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-10 bg-slate-100 rounded" />
            ))}
          </div>
        ) : grid.length === 0 ? (
          <p className="text-sm text-slate-400 py-8 text-center">
            No sync data available
          </p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100 text-left">
                    <th className="py-2 font-semibold text-slate-500">
                      Tenant
                    </th>
                    <th className="py-2 font-semibold text-slate-500">Risk</th>
                    <th className="py-2 font-semibold text-slate-500">
                      Syncs (24h)
                    </th>
                    <th className="py-2 font-semibold text-slate-500">
                      Success Rate
                    </th>
                    <th className="py-2 font-semibold text-slate-500">
                      Failed
                    </th>
                    <th className="py-2 font-semibold text-slate-500">
                      Pending
                    </th>
                    <th className="py-2 font-semibold text-slate-500">
                      Conflicts
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {grid.map((tenant: any) => (
                    <tr key={tenant.tenantId} className="hover:bg-slate-50">
                      <td className="py-2.5 font-medium text-slate-900">
                        {tenant.tenantName || tenant.tenantId}
                      </td>
                      <td className="py-2.5">
                        <span
                          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                            tenant.risk === "healthy"
                              ? "bg-green-50 text-green-600"
                              : tenant.risk === "warning"
                                ? "bg-orange-50 text-orange-600"
                                : "bg-red-50 text-red-600"
                          }`}
                        >
                          {tenant.risk || "unknown"}
                        </span>
                      </td>
                      <td className="py-2.5 text-slate-600">
                        {tenant.totalSyncs24h ?? 0}
                      </td>
                      <td className="py-2.5 text-slate-600">
                        {tenant.successRate != null
                          ? `${tenant.successRate.toFixed(1)}%`
                          : "—"}
                      </td>
                      <td className="py-2.5 text-slate-600">
                        {tenant.failedCount ?? 0}
                      </td>
                      <td className="py-2.5 text-slate-600">
                        {tenant.pendingCount ?? 0}
                      </td>
                      <td className="py-2.5 text-slate-600">
                        {tenant.unresolvedConflicts ?? 0}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {total > limit && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
                <span className="text-xs text-slate-500">
                  Page {page} of {Math.ceil(total / limit)}
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="px-3 py-1 rounded-lg text-xs font-medium bg-slate-100 text-slate-600 disabled:opacity-50"
                  >
                    Prev
                  </button>
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={page >= Math.ceil(total / limit)}
                    className="px-3 py-1 rounded-lg text-xs font-medium bg-slate-100 text-slate-600 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>

      {/* Sync Conflicts */}
      <Card>
        <h3 className="text-sm font-semibold uppercase tracking-widest text-slate-400 mb-4">
          Recent Conflicts ({conflictList.length})
        </h3>
        {conflictList.length === 0 ? (
          <p className="text-sm text-slate-400 py-8 text-center">
            No sync conflicts detected
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 text-left">
                  <th className="py-2 font-semibold text-slate-500">Tenant</th>
                  <th className="py-2 font-semibold text-slate-500">Entity</th>
                  <th className="py-2 font-semibold text-slate-500">Status</th>
                  <th className="py-2 font-semibold text-slate-500">
                    Detected At
                  </th>
                  <th className="py-2 font-semibold text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {conflictList.map((conflict: any) => (
                  <tr key={conflict.id} className="hover:bg-slate-50">
                    <td className="py-2.5 font-medium text-slate-900">
                      {conflict.tenantName || conflict.tenantId}
                    </td>
                    <td className="py-2.5 text-slate-600">
                      {conflict.entityType}/{conflict.entityId}
                    </td>
                    <td className="py-2.5">
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                          CONFLICT_STATUS_BADGE[
                            String(conflict.status).toLowerCase()
                          ] || "bg-slate-100 text-slate-500"
                        }`}
                      >
                        {conflict.status}
                      </span>
                    </td>
                    <td className="py-2.5 text-slate-600">
                      {conflict.detectedAt
                        ? new Date(conflict.detectedAt).toLocaleString()
                        : conflict.createdAt
                          ? new Date(conflict.createdAt).toLocaleString()
                          : "—"}
                    </td>
                    <td className="py-2.5">
                      {String(conflict.status).toUpperCase() === "PENDING" && (
                        <select
                          defaultValue=""
                          onChange={(event) => {
                            if (event.target.value) {
                              resolve.mutate({
                                id: conflict.id,
                                strategy: event.target.value,
                              });
                              event.target.value = "";
                            }
                          }}
                          className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs font-semibold"
                        >
                          <option value="">Resolve…</option>
                          <option value="SERVER_WINS">Server wins</option>
                          <option value="CLIENT_WINS">Client wins</option>
                          <option value="MERGE">Merge</option>
                          <option value="MANUAL">Manual</option>
                        </select>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
